import { type PayloadAction, createSlice, current } from '@reduxjs/toolkit';

import {
    type CartItem,
    type PickTwoCartItem,
    addToPickTwo,
    initPickTwo,
    removeFromPickTwo
} from '@lib/entities/Cart';
import {
    type PickTwoComboMenuItem,
    type PickTwoEligibleMenuItem,
    isPickTwoComboMenuItem
} from '@lib/entities/Menu';
import { loggedOut } from '@lib/redux/actions/lifecycle';

/**
 * Redux twin of lib/state/PickTwo.ts (JSAD-7433).
 *
 * The Zustand slice instantiates the Pick 2 pseudo cart item from the combo menu item it
 * finds in ANOTHER slice (`api.getState().menu.items`, PickTwo.ts:54-59). A reducer cannot
 * read the menu, so `startPicking` / `pick` / `remove` receive the combo item through
 * `meta.comboMenuItem`; the thin hook selects it (`isPickTwoComboMenuItem`) and, when it is
 * missing, throws the same error + Sentry report as PickTwo.ts:61-66 BEFORE dispatching. The
 * reducers therefore treat a missing item as a no-op rather than throwing.
 */
export interface PickTwoState {
    /**
     * This is the psuedo cart item for Pick Two that holds pick two selections
     */
    pickTwo?: PickTwoCartItem;

    /**
     * If defined, this item is currently being replaced, e.g. so we can
     * change menu filtering rules
     */
    replacing?: CartItem<PickTwoEligibleMenuItem>;

    /**
     * Are we currently in the middle of a pick two operation. This
     * allows the confirmatoin screens to show things like "add item"
     * instead of "add to cart" and so forth.
     */
    isSelectingPickTwoItem: boolean;
}

const initialState: PickTwoState = {
    replacing: undefined,
    pickTwo: undefined,
    isSelectingPickTwoItem: false
};

export interface ComboMenuItemMeta {
    comboMenuItem?: PickTwoComboMenuItem;
}

type ComboAction<P> = PayloadAction<P, string, ComboMenuItemMeta>;

const withComboMenuItem =
    <P>() =>
    (payload: P, comboMenuItem?: PickTwoComboMenuItem) => ({
        payload,
        meta: { comboMenuItem }
    });

/** `getPickTwo` (PickTwo.ts:83): the current pseudo item, or a fresh one from the combo item. */
const resolvePickTwo = (
    state: PickTwoState,
    comboMenuItem: PickTwoComboMenuItem | undefined
): PickTwoCartItem | undefined => {
    if (state.pickTwo) {
        return state.pickTwo;
    }

    if (!comboMenuItem || !isPickTwoComboMenuItem(comboMenuItem)) {
        return undefined;
    }

    return initPickTwo(comboMenuItem);
};

const pickTwoSlice = createSlice({
    name: 'pickTwo',
    initialState,
    reducers: {
        startPicking: {
            reducer: (state, action: ComboAction<undefined>) => {
                const pickTwo = resolvePickTwo(current(state), action.meta.comboMenuItem);

                if (!pickTwo) {
                    return;
                }

                state.isSelectingPickTwoItem = true;
                state.pickTwo = pickTwo;
            },
            prepare: (comboMenuItem?: PickTwoComboMenuItem) => ({
                payload: undefined,
                meta: { comboMenuItem }
            })
        },
        replace: (state, action: PayloadAction<CartItem<PickTwoEligibleMenuItem>>) => {
            state.replacing = action.payload;
        },
        cancelReplacement: (state) => {
            state.replacing = undefined;
        },
        pick: {
            reducer: (state, action: ComboAction<CartItem<PickTwoEligibleMenuItem>>) => {
                const pickTwo = resolvePickTwo(current(state), action.meta.comboMenuItem);

                if (!pickTwo) {
                    return;
                }

                state.pickTwo = addToPickTwo(pickTwo, action.payload);
                state.replacing = undefined;
            },
            prepare: withComboMenuItem<CartItem<PickTwoEligibleMenuItem>>()
        },
        remove: {
            reducer: (state, action: ComboAction<CartItem<PickTwoEligibleMenuItem>>) => {
                const pickTwo = resolvePickTwo(current(state), action.meta.comboMenuItem);

                if (!pickTwo) {
                    return;
                }

                state.pickTwo = removeFromPickTwo(pickTwo, action.payload);
                state.replacing = undefined;
            },
            prepare: withComboMenuItem<CartItem<PickTwoEligibleMenuItem>>()
        },
        edit: (state, action: PayloadAction<PickTwoCartItem>) => {
            state.pickTwo = action.payload;
            state.isSelectingPickTwoItem = true;
        },
        /** `reset` — surfaced by usePickTwo as `finishPicking`. */
        resetPickTwo: () => initialState
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `finishPicking()` = `reset()` (useAuth.ts:122).
        builder.addCase(loggedOut, () => initialState);
    }
});

export const { startPicking, replace, cancelReplacement, pick, remove, edit, resetPickTwo } =
    pickTwoSlice.actions;

export const selectPickTwo = (state: { pickTwo: PickTwoState }) => state.pickTwo;
export const selectPickTwoItem = (state: { pickTwo: PickTwoState }) => state.pickTwo.pickTwo;
export const selectIsSelectingPickTwoItem = (state: { pickTwo: PickTwoState }) =>
    state.pickTwo.isSelectingPickTwoItem;

export default pickTwoSlice.reducer;
